import Head from "next/head";
import { useRouter } from "next/router";
import React, { useState, useEffect } from "react";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import { useQuery } from "@apollo/client";

import { subjectsQuery } from "src/lib/graphql/schemas.queries";
import { searchProjectsQuery } from "src/lib/graphql/project.queries";
import { ReactSelectOption, SortSelectOption } from "src/lib/globalTypes";
import {
  BehaviorLanguage,
  GetSubjectsQuery,
  SearchableTypeName,
  SearchProjectsQuery,
  Subject,
} from "src/lib/graphql/types";
import { useProjectSearch } from "src/hooks/useProjectSearch";

import { socialMetadata } from "src/components/layout/Head";
import SortDropdown from "src/components/index/SortDropdown";
import PageNumbers from "src/components/PageNumbers";
import SearchResultsSidebar from "src/components/index/SearchResultsSidebar";
import SearchResultCard from "src/components/index/Search/SearchResultCard";

import NoMatches from "src/components/index/NoMatches";
import SearchResultSkeletonCard from "src/components/index/Search/SearchResultSkeletonCard";
import { NextPageContext } from "next";

const routeToType: { [key: string]: string | undefined } = {
  models: "Simulation",
  data: "Dataset",
  behaviors: "Behavior",
  schemas: "Schema",
  search: undefined,
};

const IndexSearch = () => {
  const search = useProjectSearch();
  const { query, asPath } = useRouter();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [bannerMessage, setBannerMessage] = useState("");

  // We use the searchType sent as part of the URL path to determine what type to search.
  // This is added to the query object by NextJS, thanks to the file being named [searchType]
  // We also provide for contentType used as part of the query params from older links.
  let type = query.contentType;
  if (query.searchType) {
    type = routeToType[query.searchType as string];
  }
  // Legacy provision for old links
  if (query.sort === "dateModified") {
    query.sort = "updatedAt";
  }
  if (query.sort === "datePublished") {
    query.sort = "createdAt";
  }

  // Get the search results from the API using the query params in URL
  const {
    loading: resultsLoading,
    data: resultsData,
  } = useQuery<SearchProjectsQuery>(searchProjectsQuery, {
    variables: {
      ...query,
      releasedOnly: query?.releasedOnly === "true" ? true : undefined,
      perPage: query?.perPage
        ? parseInt(query?.perPage as string, 10)
        : undefined,
      page: query?.page ? parseInt(query?.page as string, 10) : undefined,
      types: type ? [type] : undefined,
    },
    errorPolicy: "all",
  });

  const { results, totalCount, page, perPage, sort } =
    resultsData?.searchProjects || {};

  // <------------------ SUBJECT OPTIONS ----------------------->
  // Get the subjects which have any listings for the selected type
  // to populate the subject filter dropdown
  const { data: subjectsData } = useQuery<GetSubjectsQuery>(subjectsQuery, {
    variables: {
      onlyPopulated:
        type && ["Dataset", "Behavior"].includes(type as string) ? type : "All",
    },
  });

  const subjects = subjectsData?.subjects;

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [results]);

  // <-------------------- CHANGING SEARCH PARAMS ----------------------->

  // Change the content type searched for when the user changes selection
  const setType = (type: SearchableTypeName | "all"): void => {
    const subjectIsApplicable = !(type === "Simulation" || type === "Schema");
    if (!subjectIsApplicable && query.subject) {
      setBannerMessage(`We've removed your 'relates to' filter
      - it only applies to data and behaviors.`);
      setTimeout(() => setBannerMessage(""), 8000);
    }
    search({
      searchType: type,
      // Clear filters which are inappropriate to the selected type
      subject: subjectIsApplicable ? query.subject : undefined,
      language:
        type !== "Behavior" ? undefined : (query.language as BehaviorLanguage),
      temporalCoverage:
        type !== "Dataset" ? undefined : (query.temporalCoverage as string),
      dataFrequency:
        type !== "Dataset" ? undefined : (query.dataFrequency as string),
      spatialCoverage:
        type !== "Dataset" ? undefined : (query.spatialCoverage as string),
      releasedOnly:
        type !== "Schema" ? (query.releasedOnly as string) : undefined,
    });
  };

  const setFilter = (key: string, value?: string) => search({ [key]: value });

  const setSort = (sortOption: any) => {
    search({ sort: sortOption.value });
  };

  const setPage = (pageNo: number) => {
    search({ page: pageNo });
  };

  // Setting the 'x of y results' label
  let resultString = "";
  if (!totalCount) {
    resultString = "0 results";
  } else if (totalCount === 1) {
    resultString = "1 result";
  } else if (perPage && page) {
    const start = perPage * (page - 1) + 1;
    const end = Math.min(start + perPage - 1, totalCount);
    resultString = `${start} - ${end} of ${totalCount} results`;
  }

  const sortOptions: SortSelectOption[] = [
    {
      value: "popularity",
      label: "Most popular",
    },
    {
      value: "updatedAt",
      label: "Last modified",
    },
    {
      value: "createdAt",
      label: "Last created",
    },
  ];
  if (query.query) {
    sortOptions.push({
      value: "relevance",
      label: "Most relevant",
    });
  }

  const selectedSort = sortOptions.find(
    (option) => option.value === (query.sort || sort || "dateModified")
  );

  const SideDrawer = (
    <SearchResultsSidebar
      subjects={(subjects as Subject[]) || []}
      query={query}
      setType={setType}
      setFilter={setFilter}
      bannerMessage={bannerMessage}
    />
  );

  const iOS =
    typeof window !== "undefined" &&
    /iPad|iPhone|iPod/.test(navigator.userAgent);

  return (
    <>
      <Head>
        {socialMetadata({
          title: "Search results - HASH",
          url: "https://hash.ai" + asPath,
        })}
      </Head>
      <div className="search-results-page-wrapper">
        <div className="search-results-content container">
          <div className="desktop-sidedrawer content-min-height">
            {SideDrawer}
          </div>

          <div className="mobile-sidedrawer">
            <SwipeableDrawer
              disableBackdropTransition={!iOS}
              disableDiscovery={iOS}
              open={mobileDrawerOpen}
              onClose={() => setMobileDrawerOpen(false)}
              onOpen={() => setMobileDrawerOpen(true)}
              transitionDuration={500}
            >
              {SideDrawer}
            </SwipeableDrawer>
          </div>

          <div className="search-results-main">
            <div className="count-and-sort">
              <div className="result-count">
                {!resultsLoading && resultString}
              </div>
              {totalCount! > 0 && !resultsLoading && (
                <SortDropdown
                  options={sortOptions as ReactSelectOption[]}
                  value={selectedSort as ReactSelectOption}
                  onChange={setSort}
                />
              )}
              <button
                className="filter-drawer-toggle"
                onClick={() => setMobileDrawerOpen(!mobileDrawerOpen)}
              >
                Filter +/-
              </button>
            </div>

            <div className="results-list">
              {results?.length === 0 && <NoMatches />}
              {resultsLoading && (
                <>
                  <SearchResultSkeletonCard />
                  <SearchResultSkeletonCard />
                  <SearchResultSkeletonCard />
                </>
              )}
              {results?.map((result) => (
                <SearchResultCard result={result} key={result.id} />
              ))}
              {perPage && totalCount! > perPage && (
                <div className="paginate">
                  <PageNumbers
                    page={page || 1}
                    totalRecords={totalCount || 0}
                    perPage={perPage}
                    setPage={setPage}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default IndexSearch;

IndexSearch.getInitialProps = async ({ query, res }: NextPageContext) => {
  const { searchType } = query;

  if (!searchType || !Object.keys(routeToType).includes(searchType as string)) {
    if (res) {
      res.writeHead(301, { Location: "/404" });
      res.end();
    } else {
      window.location.replace("/404");
    }
  }
};
